import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const PERFIL_EMPTY = {
  id: null,
  nome: '',
  erp_name: '',
  db_type: 'firebird',
  products_query: '',
  estoque_query: '',
  vendas_query: '',
};

const FONTE_EMPTY = {
  id: null,
  nome: '',
  empresa_id: '',
  erp_name: '',
  db_type: 'firebird',
  schedule_minutes: 360,
  sla_minutes: '',
  mapping_profile_id: '',
  connection_json_text: '{\n  "host": "127.0.0.1",\n  "port": 3050,\n  "database": "C:/dados/cliente.fdb",\n  "user": "SYSDBA",\n  "password": "masterkey"\n}',
};

function parseJsonSafe(text) {
  try {
    return { ok: true, value: JSON.parse(text || '{}') };
  } catch (error) {
    return { ok: false, error: error.message };
  }
}

const IntegracoesSync = () => {
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [perfis, setPerfis] = useState([]);
  const [fontes, setFontes] = useState([]);
  const [runs, setRuns] = useState([]);
  const [slaMonitor, setSlaMonitor] = useState({ resumo: { abertos: 0, resolvidos: 0 }, itens: [] });
  const [empresas, setEmpresas] = useState([]);

  const [perfilForm, setPerfilForm] = useState(PERFIL_EMPTY);
  const [fonteForm, setFonteForm] = useState(FONTE_EMPTY);
  const [agentTokenModal, setAgentTokenModal] = useState(null);

  const loadData = async () => {
    try {
      setLoading(true);
      const [templatesRs, perfisRs, fontesRs, runsRs, slaRs, empresasRs] = await Promise.all([
        axios.get('/api/admin/integracoes/perfis/templates'),
        axios.get('/api/admin/integracoes/perfis'),
        axios.get('/api/admin/integracoes/fontes'),
        axios.get('/api/admin/integracoes/runs'),
        axios.get('/api/admin/integracoes/monitor/sla?status=open'),
        axios.get('/api/admin/empresas'),
      ]);
      setTemplates(Array.isArray(templatesRs.data) ? templatesRs.data : []);
      setPerfis(Array.isArray(perfisRs.data) ? perfisRs.data : []);
      setFontes(Array.isArray(fontesRs.data) ? fontesRs.data : []);
      setRuns(Array.isArray(runsRs.data) ? runsRs.data : []);
      setSlaMonitor(slaRs.data || { resumo: { abertos: 0, resolvidos: 0 }, itens: [] });
      setEmpresas(Array.isArray(empresasRs.data) ? empresasRs.data : []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar integracoes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const applyTemplate = (tpl) => {
    setPerfilForm({
      id: null,
      nome: tpl.nome || '',
      erp_name: tpl.erp_name || '',
      db_type: tpl.db_type || 'firebird',
      products_query: tpl.products_query || '',
      estoque_query: tpl.estoque_query || '',
      vendas_query: tpl.vendas_query || '',
    });
    toast.success(`Template aplicado: ${tpl.nome}`);
  };

  const editarPerfil = (perfil) => {
    setPerfilForm({
      id: perfil.id,
      nome: perfil.nome || '',
      erp_name: perfil.erp_name || '',
      db_type: perfil.db_type || 'firebird',
      products_query: perfil.products_query || '',
      estoque_query: perfil.estoque_query || '',
      vendas_query: perfil.vendas_query || '',
    });
  };

  const editarFonte = (fonte) => {
    setFonteForm({
      id: fonte.id,
      nome: fonte.nome || '',
      empresa_id: fonte.empresa_id || '',
      erp_name: fonte.erp_name || '',
      db_type: fonte.db_type || 'firebird',
      schedule_minutes: fonte.schedule_minutes || 360,
      sla_minutes: fonte.sla_minutes || '',
      mapping_profile_id: fonte.mapping_profile_id || '',
      connection_json_text: JSON.stringify(fonte.connection_json || {}, null, 2) || '{}',
    });
  };

  const limparPerfil = () => setPerfilForm(PERFIL_EMPTY);
  const limparFonte = () => setFonteForm(FONTE_EMPTY);

  const salvarPerfil = async () => {
    if (!perfilForm.nome.trim() || !perfilForm.products_query.trim() || !perfilForm.estoque_query.trim() || !perfilForm.vendas_query.trim()) {
      toast.error('Preencha nome e as 3 queries do perfil');
      return;
    }
    try {
      const payload = {
        ...perfilForm,
        nome: perfilForm.nome.trim(),
        erp_name: perfilForm.erp_name?.trim() || null,
      };
      if (perfilForm.id) {
        await axios.put(`/api/admin/integracoes/perfis/${perfilForm.id}`, payload);
        toast.success('Perfil atualizado');
      } else {
        await axios.post('/api/admin/integracoes/perfis', payload);
        toast.success('Perfil criado');
      }
      limparPerfil();
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar perfil');
    }
  };

  const excluirPerfil = async (id) => {
    if (!window.confirm('Deseja excluir este perfil?')) return;
    try {
      await axios.delete(`/api/admin/integracoes/perfis/${id}`);
      toast.success('Perfil excluido');
      if (perfilForm.id === id) limparPerfil();
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao excluir perfil');
    }
  };

  const salvarFonte = async () => {
    if (!fonteForm.nome.trim() || !fonteForm.empresa_id || !fonteForm.mapping_profile_id) {
      toast.error('Preencha nome, empresa e perfil');
      return;
    }

    const parsed = parseJsonSafe(fonteForm.connection_json_text);
    if (!parsed.ok) {
      toast.error(`JSON de conexao invalido: ${parsed.error}`);
      return;
    }

    try {
      const payload = {
        nome: fonteForm.nome.trim(),
        empresa_id: fonteForm.empresa_id,
        erp_name: fonteForm.erp_name?.trim() || null,
        db_type: fonteForm.db_type,
        mapping_profile_id: fonteForm.mapping_profile_id,
        schedule_minutes: Number(fonteForm.schedule_minutes) || 360,
        sla_minutes: fonteForm.sla_minutes ? Number(fonteForm.sla_minutes) : null,
        connection_json: parsed.value,
      };
      if (fonteForm.id) {
        await axios.put(`/api/admin/integracoes/fontes/${fonteForm.id}`, payload);
        toast.success('Fonte atualizada');
      } else {
        await axios.post('/api/admin/integracoes/fontes', payload);
        toast.success('Fonte criada');
      }
      limparFonte();
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar fonte');
    }
  };

  const excluirFonte = async (id) => {
    if (!window.confirm('Deseja excluir esta fonte?')) return;
    try {
      await axios.delete(`/api/admin/integracoes/fontes/${id}`);
      toast.success('Fonte excluida');
      if (fonteForm.id === id) limparFonte();
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao excluir fonte');
    }
  };

  const rodarSync = async (sourceId) => {
    try {
      await axios.post(`/api/admin/integracoes/fontes/${sourceId}/sync`);
      toast.success('Sync manual executado');
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao executar sync');
    }
  };

  const gerarTokenAgente = async (sourceId) => {
    try {
      const { data } = await axios.post(`/api/admin/integracoes/fontes/${sourceId}/agent/rotate-token`);
      setAgentTokenModal({
        fonte: data?.fonte?.nome || 'Fonte',
        token: data?.agent_token || '',
      });
      toast.success('Token do agente gerado');
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao gerar token do agente');
    }
  };

  const desabilitarAgente = async (sourceId) => {
    if (!window.confirm('Desabilitar agente local desta fonte?')) return;
    try {
      await axios.post(`/api/admin/integracoes/fontes/${sourceId}/agent/disable`);
      toast.success('Agente desabilitado');
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao desabilitar agente');
    }
  };

  return (
    <div className="animate-fade-in space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Integracoes e Cron</h1>
          <p className="text-gray-600">Gerencie perfis de extracao, fontes por empresa e execucoes de sincronizacao</p>
        </div>
        <button className="btn-secondary" onClick={loadData} disabled={loading}>
          Atualizar
        </button>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold">Templates de Perfil</h3>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {templates.map((tpl, idx) => (
              <button key={`${tpl.nome}-${idx}`} className="text-left p-3 border border-gray-200 rounded-lg hover:bg-gray-50" onClick={() => applyTemplate(tpl)}>
                <p className="font-semibold text-gray-900">{tpl.nome}</p>
                <p className="text-xs text-gray-500 mt-1">
                  ERP: {tpl.erp_name || '-'} | Banco: {tpl.db_type}
                </p>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="card">
          <div className="card-header flex items-center justify-between">
            <h3 className="text-lg font-semibold">{perfilForm.id ? 'Editar Perfil de Mapeamento' : 'Novo Perfil de Mapeamento'}</h3>
            {perfilForm.id && (
              <button className="btn-secondary" onClick={limparPerfil}>
                Cancelar edicao
              </button>
            )}
          </div>
          <div className="card-body space-y-3">
            <input className="form-input" placeholder="Nome do perfil" value={perfilForm.nome} onChange={(e) => setPerfilForm({ ...perfilForm, nome: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <input className="form-input" placeholder="ERP (ex.: syspdv)" value={perfilForm.erp_name} onChange={(e) => setPerfilForm({ ...perfilForm, erp_name: e.target.value })} />
              <select className="form-select" value={perfilForm.db_type} onChange={(e) => setPerfilForm({ ...perfilForm, db_type: e.target.value })}>
                <option value="firebird">Firebird</option>
                <option value="postgres">Postgres</option>
                <option value="sqlserver">SQL Server</option>
                <option value="mysql">MySQL</option>
              </select>
            </div>
            <div>
              <label className="form-label">Query Produtos</label>
              <textarea className="form-textarea" rows="4" value={perfilForm.products_query} onChange={(e) => setPerfilForm({ ...perfilForm, products_query: e.target.value })} />
            </div>
            <div>
              <label className="form-label">Query Estoque</label>
              <textarea className="form-textarea" rows="4" value={perfilForm.estoque_query} onChange={(e) => setPerfilForm({ ...perfilForm, estoque_query: e.target.value })} />
            </div>
            <div>
              <label className="form-label">Query Vendas</label>
              <textarea className="form-textarea" rows="4" value={perfilForm.vendas_query} onChange={(e) => setPerfilForm({ ...perfilForm, vendas_query: e.target.value })} />
            </div>
            <button className="btn-primary" onClick={salvarPerfil}>
              {perfilForm.id ? 'Atualizar Perfil' : 'Salvar Perfil'}
            </button>
          </div>
        </div>

        <div className="card">
        <div className="card-header flex items-center justify-between">
          <h3 className="text-lg font-semibold">{fonteForm.id ? 'Editar Fonte de Integracao' : 'Nova Fonte de Integracao'}</h3>
          {fonteForm.id && (
            <button className="btn-secondary" onClick={limparFonte}>
                Cancelar edicao
              </button>
            )}
          </div>
          <div className="card-body space-y-3">
            {fonteForm.id && (
              <div>
                <label className="form-label">UUID da Fonte</label>
                <div className="flex gap-2">
                  <input className="form-input font-mono text-xs" readOnly value={fonteForm.id} />
                  <button className="btn-secondary" onClick={() => navigator.clipboard?.writeText(fonteForm.id)}>
                    Copiar
                  </button>
                </div>
              </div>
            )}
            <input className="form-input" placeholder="Nome da fonte (ex.: Loja 01 Firebird)" value={fonteForm.nome} onChange={(e) => setFonteForm({ ...fonteForm, nome: e.target.value })} />
            <div className="grid grid-cols-2 gap-3">
              <select className="form-select" value={fonteForm.empresa_id} onChange={(e) => setFonteForm({ ...fonteForm, empresa_id: e.target.value })}>
                <option value="">Selecione empresa</option>
                {empresas.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.nome}
                  </option>
                ))}
              </select>
              <input className="form-input" placeholder="ERP (ex.: syspdv)" value={fonteForm.erp_name} onChange={(e) => setFonteForm({ ...fonteForm, erp_name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <select className="form-select" value={fonteForm.db_type} onChange={(e) => setFonteForm({ ...fonteForm, db_type: e.target.value })}>
                <option value="firebird">Firebird</option>
                <option value="postgres">Postgres</option>
                <option value="sqlserver">SQL Server</option>
                <option value="mysql">MySQL</option>
              </select>
              <input
                type="number"
                className="form-input"
                placeholder="Periodicidade em minutos"
                value={fonteForm.schedule_minutes}
                onChange={(e) => setFonteForm({ ...fonteForm, schedule_minutes: e.target.value })}
              />
              <input
                type="number"
                className="form-input"
                placeholder="SLA sem sync (min)"
                value={fonteForm.sla_minutes}
                onChange={(e) => setFonteForm({ ...fonteForm, sla_minutes: e.target.value })}
              />
            </div>
            <select className="form-select" value={fonteForm.mapping_profile_id} onChange={(e) => setFonteForm({ ...fonteForm, mapping_profile_id: e.target.value })}>
              <option value="">Selecione perfil</option>
              {perfis.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.nome} ({p.db_type})
                </option>
              ))}
            </select>
            <div>
              <label className="form-label">connection_json</label>
              <textarea className="form-textarea font-mono text-xs" rows="8" value={fonteForm.connection_json_text} onChange={(e) => setFonteForm({ ...fonteForm, connection_json_text: e.target.value })} />
            </div>
            <button className="btn-primary" onClick={salvarFonte}>
              {fonteForm.id ? 'Atualizar Fonte' : 'Salvar Fonte'}
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold">Perfis Cadastrados</h3>
        </div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>ERP</th>
                  <th>Banco</th>
                  <th>Acoes</th>
                </tr>
              </thead>
              <tbody>
                {perfis.map((p) => (
                  <tr key={p.id}>
                    <td className="font-medium">{p.nome}</td>
                    <td>{p.erp_name || '-'}</td>
                    <td>{p.db_type}</td>
                    <td>
                      <div className="flex gap-2">
                        <button className="btn-secondary" onClick={() => editarPerfil(p)}>
                          Editar
                        </button>
                        <button className="btn-danger" onClick={() => excluirPerfil(p.id)}>
                          Excluir
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!perfis.length && (
                  <tr>
                    <td colSpan={4} className="text-center text-gray-500 py-6">
                      Nenhum perfil cadastrado
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold">Fontes Cadastradas</h3>
        </div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Fonte</th>
                  <th>UUID</th>
                  <th>Empresa</th>
                  <th>Banco/ERP</th>
                  <th>Agente Local</th>
                  <th>Agenda</th>
                  <th>SLA</th>
                  <th>Ultimo Sync</th>
                  <th>Acoes</th>
                </tr>
              </thead>
              <tbody>
                {fontes.map((f) => (
                  <tr key={f.id}>
                    <td className="font-medium">{f.nome}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        <code className="text-xs">{String(f.id || '').slice(0, 8)}...</code>
                        <button className="btn-secondary" onClick={() => navigator.clipboard?.writeText(String(f.id || ''))}>
                          Copiar
                        </button>
                      </div>
                    </td>
                    <td>{f.empresa_nome}</td>
                    <td>
                      {f.db_type} / {f.erp_name || '-'}
                    </td>
                    <td>
                      <span className={f.agent_enabled ? 'badge-success' : 'badge-warning'}>{f.agent_enabled ? 'Habilitado' : 'Desabilitado'}</span>
                    </td>
                    <td>{f.schedule_minutes} min</td>
                    <td>{f.sla_minutes ? `${f.sla_minutes} min` : '-'}</td>
                    <td>{f.last_sync_at ? new Date(f.last_sync_at).toLocaleString('pt-BR') : '-'}</td>
                    <td>
                      <div className="flex gap-2">
                        <button className="btn-secondary" onClick={() => gerarTokenAgente(f.id)}>
                          Token Agente
                        </button>
                        <button className="btn-secondary" onClick={() => desabilitarAgente(f.id)} disabled={!f.agent_enabled}>
                          Desabilitar Agente
                        </button>
                        <button className="btn-secondary" onClick={() => rodarSync(f.id)}>
                          Rodar agora
                        </button>
                        <button className="btn-secondary" onClick={() => editarFonte(f)}>
                          Editar
                        </button>
                        <button className="btn-danger" onClick={() => excluirFonte(f.id)}>
                          Excluir
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!fontes.length && (
                  <tr>
                    <td colSpan={9} className="text-center text-gray-500 py-6">
                      Nenhuma fonte cadastrada
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header flex items-center justify-between">
          <h3 className="text-lg font-semibold">Monitor SLA de Sincronizacao</h3>
          <div className="text-sm text-gray-600">
            Abertos: <span className="font-semibold text-danger-700">{slaMonitor?.resumo?.abertos || 0}</span>
          </div>
        </div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Empresa</th>
                  <th>Fonte</th>
                  <th>Sem sync</th>
                  <th>SLA</th>
                  <th>Aberto em</th>
                  <th>Mensagem</th>
                </tr>
              </thead>
              <tbody>
                {(slaMonitor?.itens || []).map((item) => (
                  <tr key={item.id}>
                    <td>{item.empresa_nome}</td>
                    <td>{item.source_nome}</td>
                    <td>{item.minutes_without_sync} min</td>
                    <td>{item.sla_minutes} min</td>
                    <td>{item.opened_at ? new Date(item.opened_at).toLocaleString('pt-BR') : '-'}</td>
                    <td className="text-sm text-gray-700">{item.message}</td>
                  </tr>
                ))}
                {!(slaMonitor?.itens || []).length && (
                  <tr>
                    <td colSpan={6} className="text-center text-gray-500 py-6">
                      Nenhuma fonte fora do SLA no momento.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold">Ultimas Execucoes</h3>
        </div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Inicio</th>
                  <th>Fonte</th>
                  <th>Trigger</th>
                  <th>Status</th>
                  <th>Duracao</th>
                  <th>Detalhes</th>
                </tr>
              </thead>
              <tbody>
                {runs.map((r) => (
                  <tr key={r.id}>
                    <td>{r.started_at ? new Date(r.started_at).toLocaleString('pt-BR') : '-'}</td>
                    <td>{r.source_nome}</td>
                    <td>{r.trigger_type}</td>
                    <td>
                      <span className={r.status === 'success' ? 'badge-success' : r.status === 'error' ? 'badge-danger' : 'badge-warning'}>{r.status}</span>
                    </td>
                    <td>{r.duration_ms ? `${r.duration_ms} ms` : '-'}</td>
                    <td className="text-xs text-gray-600">{r.error_message || JSON.stringify(r.stats_json || {})}</td>
                  </tr>
                ))}
                {!runs.length && (
                  <tr>
                    <td colSpan={6} className="text-center text-gray-500 py-6">
                      Sem execucoes registradas
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {agentTokenModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl p-6 space-y-4">
            <h3 className="text-xl font-semibold text-gray-900">Token do Agente Local</h3>
            <p className="text-sm text-gray-600">
              Fonte: <span className="font-medium">{agentTokenModal.fonte}</span>
            </p>
            <p className="text-sm text-danger-700">Guarde este token agora. Ele nao sera exibido novamente.</p>
            <textarea className="form-textarea font-mono text-xs" rows="3" readOnly value={agentTokenModal.token} />
            <div className="flex justify-end gap-2">
              <button className="btn-secondary" onClick={() => navigator.clipboard?.writeText(agentTokenModal.token)}>
                Copiar token
              </button>
              <button className="btn-primary" onClick={() => setAgentTokenModal(null)}>
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntegracoesSync;
